/**
 * What to do when one turn's chat stream ends.
 *
 * Every send opens its own stream, and the store's streaming fields
 * (isStreaming, streamingContent) belong to whichever turn is current. A
 * stream must only ever report on itself: an older turn's stream that closes
 * while a newer turn is still in its tool rounds used to read the newer
 * turn's `isStreaming: true` and empty content as "my connection dropped",
 * clear its spinner and show "something went wrong" above a reply that then
 * arrived fine (dev 2026-10-07: the server kept streams open 36–82 s after
 * their `done`, so the overlap was routine).
 */

export interface StreamEndFacts {
  /** This stream delivered its own `done`. */
  sawDone: boolean;
  /** This stream delivered an `error` frame — already surfaced. */
  sawError: boolean;
  /** No newer turn has started since this one. */
  isCurrentTurn: boolean;
  /** The turn streamed some text before the stream ended. */
  hasContent: boolean;
}

/**
 * - `none`: nothing to do — the turn ended itself (`done` / `error`), or a newer turn owns the screen.
 * - `finalize`: the stream dropped after text arrived — keep what came through.
 * - `dropped`: the stream dropped before any text — clear the spinner, offer a retry.
 */
export type StreamEnding = 'none' | 'finalize' | 'dropped';

export function streamEnding(f: StreamEndFacts): StreamEnding {
  if (f.sawDone || f.sawError || !f.isCurrentTurn) return 'none';
  return f.hasContent ? 'finalize' : 'dropped';
}
